import { FieldTypes, ActionConfigs, Sizes, Types, Align, Backdrop, ModalDefaults, TextAreaLabelPlacements } from '../Constants';
import { html, markTrusted } from '../../ContentComposer/ContentComposer';
import { decodeHtmlEntities } from '../ModalUtils';
import { ModalButtonBuilder } from './ModalButtonBuilder.js';
import { RadioOptionBuilder } from './RadioOptionBuilder.js';
import { GenericRowBuilder } from './GenericRowBuilder.js';
import { InputAppendBuilder } from './InputAppendBuilder.js';
import { TableRegistry } from './TableRegistry.js';
import { TableHandle } from './TableHandle.js';

export class GenericModalBuilder {
    constructor(options = {}, modalClass) {
        this.ModalClass = modalClass; // Pass the GenericModal class to avoid circular imports
        this.settings = {
            type: 'warning',
            title: '',
            body: '',
            acceptText: 'OK',
            rejectText: 'Cancel',
            onAccept: null,
            onReject: null,
            onShown: null,
            onHidden: null,
            showAccept: true,
            showReject: true,
            width: null,
            height: null,
            size: null,
            backdrop: true,
            keyboard: true,
            header: true,
            footer: true,
            buttons: null,
            dialogStyle: {},
            contentStyle: {},
            bodyStyle: {},
            headerStyle: {},
            footerStyle: {},
            footerAlign: 'right',
            labelCol: ModalDefaults.labelCol,
            inputCol: ModalDefaults.inputCol,
            labelAlign: ModalDefaults.labelAlign,
            textAreaLabelPlacement: ModalDefaults.textAreaLabelPlacement,
            items: [],
            processAction: null,
            isBatch: false,
            batchColumns: null,
            _onShownHooks: [],
            _onHiddenHooks: [],
            ...options
        };
        this.lastTableId = null;
        this._lastField = null;
        this._formData = null;
        this._dataMapping = {};

        // ── Table registry ────────────────────────────────────────────────────
        this._isShown = false;
        this.tables = new TableRegistry();

        // Flip _isShown when the modal opens / closes so TableHandle.isShown works
        this.settings._onShownHooks.push(() => { this._isShown = true; });
        this.settings._onHiddenHooks.push(() => { this._isShown = false; });
    }

    withTitle(title) {
        this.settings.title = title;
        return this;
    }

    withBody(body) {
        this.settings.body = body;
        return this;
    }

    addHtml(html) {
        if (!this.settings._bodyFragments) this.settings._bodyFragments = [];
        this.settings._bodyFragments.push(html);
        if (!this.settings.items) this.settings.items = [];
        this.settings.items.push({ type: 'html', html });
        return this;
    }

    /**
     * Set body content from spread trusted fragments (text, header, lists, etc).
     * First item becomes the primary body; the rest become body fragments.
     */
    content(...items) {
        if (items.length > 0) {
            this.settings.body = items[0];
            this.settings._bodyFragments = items.slice(1);
        }
        return this;
    }

    addIcon(iconClass, style = {}) {
        const defaultStyle = { 'font-size': '50px', 'display': 'block', 'margin-bottom': '15px' };
        const mergedStyle = { ...defaultStyle, ...style };
        const styleStr = Object.entries(mergedStyle).map(([k, v]) => `${k}:${v}`).join(';');
        return this.addHtml(html`<i class="bi ${iconClass}" style="${styleStr}"></i>`);
    }

    _applyDataTableConfigurator(builder, configurator) {
        const result = configurator(builder);
        if (typeof result === 'function' && result.name && builder[result.name] === result) {
            result.call(builder);
        }
        return builder;
    }

    _registerDataTableSetup(tableId, optionsOrFn) {
        return this.onShown(() => {
            const selector = '#' + tableId;
            if (typeof optionsOrFn === 'function' && window.DataTableBuilder) {
                const builder = window.DataTableBuilder.create(selector);
                this._applyDataTableConfigurator(builder, optionsOrFn);
                const adapter = builder.build();
                if (adapter && adapter.instance) {
                    // Expose the DataTableAdapter so modal consumers can call
                    // adapter.getSelectedData() instead of the raw DataTables API
                    window.jQuery(selector).data('dt-adapter', adapter);
                    setTimeout(() => adapter.instance.columns.adjust(), 100);
                }
                return;
            }

            if (window.DataTableBuilder) {
                const adapter = window.DataTableBuilder.create(selector).withOptions(optionsOrFn).build();
                if (adapter && adapter.instance) {
                    window.jQuery(selector).data('dt-adapter', adapter);
                    setTimeout(() => adapter.instance.columns.adjust(), 100);
                }
            } else if (window.jQuery && window.jQuery.fn.DataTable) {
                const dt = window.jQuery(selector).DataTable(optionsOrFn);
                setTimeout(() => dt.columns.adjust(), 100);
            }
        });
    }

    addTable(id, arg2, arg3 = {}, arg4 = null) {
        this.lastTableId = id;
        let headerTitle = null;
        let headerOptions = null;
        let columns = arg2;
        let options = arg3;
        let tableConfigurator = null;
        let dataRows = arg4;

        // Signature: addTable(id, headerTextOrObject, columnsArray, dataRowsArray)
        if (typeof arg2 === 'string' || (typeof arg2 === 'object' && arg2 !== null && (arg2.title || arg2.text || arg2.background || arg2.bg))) {
            if (Array.isArray(arg3)) {
                headerTitle = typeof arg2 === 'string' ? arg2 : (arg2.title || arg2.text || '');
                headerOptions = typeof arg2 === 'object' ? arg2 : {};
                columns = arg3;
                dataRows = arg4;
                options = {};
            }
        }

        if (typeof columns === 'function') {
            tableConfigurator = columns;
            columns = [];
            options = {};
        } else if (typeof options === 'function') {
            tableConfigurator = options;
            options = {};
        } else if (Array.isArray(options)) {
            dataRows = options;
            options = {};
        }

        const cls = options.class || 'gm-table table table-bordered table-striped table-sm text-center mb-0';
        const style = options.style || 'width: 100%;';

        const rawColumns = Array.isArray(columns) ? columns : [];
        const colSpan = Math.max(1, rawColumns.length);

        let tableHeaderRow = null;
        if (headerTitle) {
            const bg = (headerOptions && (headerOptions.background || headerOptions.bg)) || '#0084ff';
            const color = (headerOptions && (headerOptions.textColor || headerOptions.color)) || '#ffffff';
            tableHeaderRow = html`
                <tr>
                    <th colspan="${colSpan}" style="background: ${bg}; color: ${color}; text-align: left; padding: 8px 12px; font-weight: 700; text-transform: uppercase; font-size: 0.85rem; border-bottom: none;">
                        ${headerTitle}
                    </th>
                </tr>`;
        }

        const headers = rawColumns.length > 0
            ? rawColumns.map(col => {
                const rawTitle = typeof col === 'object' ? (col.title || '') : col;
                const title = typeof rawTitle === 'string' ? decodeHtmlEntities(rawTitle) : rawTitle;
                const colStyle = typeof col === 'object' && col.style ? ` style="${col.style}"` : '';
                return html`<th${colStyle}>${title}</th>`;
            })
            : null;

        const thead = headers
            ? html`<thead class="table-light">${tableHeaderRow || ''}<tr>${headers}</tr></thead>`
            : (tableHeaderRow ? html`<thead class="table-light">${tableHeaderRow}</thead>` : html`<thead><tr></tr></thead>`);

        let tbody = html`<tbody></tbody>`;
        if (Array.isArray(dataRows) && dataRows.length > 0 && rawColumns.length > 0) {
            const trs = dataRows.map(row => {
                let tds = [];
                if (Array.isArray(row)) {
                    tds = row.map(val => {
                        const cellContent = typeof val === 'string' && val.includes('<') ? markTrusted(val) : (val !== undefined && val !== null ? val : '');
                        return html`<td>${cellContent}</td>`;
                    });
                } else if (typeof row === 'object' && row !== null) {
                    tds = rawColumns.map(col => {
                        const colKey = typeof col === 'object' ? col.data || col.title || '' : col;
                        const cellVal = row[colKey] !== undefined ? row[colKey] : (row[col] !== undefined ? row[col] : '');
                        const cellContent = typeof cellVal === 'string' && cellVal.includes('<') ? markTrusted(cellVal) : cellVal;
                        return html`<td>${cellContent}</td>`;
                    });
                }
                return html`<tr>${tds}</tr>`;
            });
            tbody = html`<tbody>${trs}</tbody>`;
        } else if (Array.isArray(dataRows) && dataRows.length === 0) {
            tbody = html`<tbody><tr><td colspan="${colSpan}" class="text-center text-muted">No data available</td></tr></tbody>`;
        }

        this.addHtml(html`
            <div class="table-responsive mb-3">
                <table id="${id}" class="${cls}" style="${style}">
                    ${thead}
                    ${tbody}
                </table>
            </div>`);

        if (tableConfigurator) {
            this._registerDataTableSetup(id, tableConfigurator);
        }

        // Register in the table registry so callers can do adapter.tables.get(id)
        const handle = new TableHandle(
            id,
            headerTitle ? (headerOptions && Object.keys(headerOptions).length ? { title: headerTitle, ...headerOptions } : headerTitle) : null,
            rawColumns,
            Array.isArray(dataRows) ? dataRows : [],
            { isShown: () => this._isShown }
        );
        this.tables._register(id, handle);

        return this;
    }

    addMessage(text, options = {}) {
        const tag = options.tag || 'p';
        const cls = options.class || '';
        const style = options.style || '';
        const content = html`<${tag} class="${cls}" style="${style}">${text}</${tag}>`;
        return this.addHtml(content);
    }

    withCenteredBody(centered) {
        if (centered) {
            this.withBodyStyle({ 'text-align': 'center', 'padding': '30px 20px' });
        }
        return this;
    }

    withType(type) {
        const validTypes = Object.values(Types);
        if (validTypes.includes(type)) {
            this.settings.type = type;
        } else {
            console.warn(`[GenericModal] Invalid type: "${type}". Valid types are: ${validTypes.join(', ')}`);
        }
        return this;
    }

    asBatch() {
        this.settings.isBatch = true;
        return this;
    }

    withItems(items) {
        this.settings.items = items || [];
        return this;
    }

    addBatchTable(columns) {
        this.settings.batchColumns = columns;
        const tableId = 'gm-batch-table';
        const cols = [
            ...columns.map(c => ({ title: c.title, style: c.style })),
            { title: 'Status', style: 'width: 100px; text-align: center;' }
        ];
        return this.addTable(tableId, cols);
    }

    onProcess(callback) {
        this.settings.processAction = callback;
        return this;
    }

    onTableAction(tableId, selector, callback) {
        if (typeof tableId === 'string' && (typeof selector === 'function' || typeof selector === 'string')) {
            if (typeof selector === 'function') {
                callback = selector;
                selector = tableId;
                tableId = this.lastTableId;
            }
        }

        const selectorMap = {
            'view':     '.btn-view, .btn-view-ar, .view-action, .gm-btn-view',
            'edit':     '.btn-edit, .edit-action, .gm-btn-edit',
            'delete':   '.btn-delete, .delete-action, .gm-btn-delete',
            'accept':   '.btn-accept, .gm-btn-accept-row',
            'reject':   '.btn-reject, .gm-btn-reject-row',
            'download': '.gm-btn-download',
            'print':    '.gm-btn-print',
            'info':     '.gm-btn-info'
        };

        const finalSelector = selectorMap[selector.toLowerCase()] || selector;

        if (!this.settings._tableActions) this.settings._tableActions = [];
        this.settings._tableActions.push({ tableId, selector: finalSelector, callback });
        return this;
    }

    setupDataTable(optionsOrFn) {
        const tableId = this.lastTableId;
        return this._registerDataTableSetup(tableId, optionsOrFn);
    }

    withSize(size) {
        const validSizes = Object.values(Sizes);
        if (validSizes.includes(size)) {
            this.settings.size = size;
        } else {
            console.warn(`[GenericModal] Invalid size: "${size}". Valid sizes are: ${validSizes.join(', ')}`);
        }
        return this;
    }

    withWidth(width) {
        this.settings.width = width;
        return this;
    }

    withAcceptText(text) {
        this.settings.acceptText = text;
        return this;
    }

    withRejectText(text) {
        this.settings.rejectText = text;
        return this;
    }

    withButtons(buttonsOrFn) {
        if (typeof buttonsOrFn === 'function') {
            const builder = new ModalButtonBuilder();
            buttonsOrFn(builder);
            this.settings.buttons = builder._getButtons();
        } else {
            this.settings.buttons = buttonsOrFn;
        }
        return this;
    }

    addButton(id, text, onClickOrOptions, options = {}) {
        if (!id) {
            throw new Error('[GenericModalBuilder] "id" parameter is required as the first argument to addButton().');
        }

        let onClick = null;
        let opts = options;

        if (typeof onClickOrOptions === 'function') {
            onClick = onClickOrOptions;
        } else if (typeof onClickOrOptions === 'object' && onClickOrOptions !== null) {
            opts = onClickOrOptions;
            if (opts.onClick) onClick = opts.onClick;
        }

        const btnConfig = {
            id,
            text: text || 'Button',
            class: opts.class || 'gm-btn-secondary',
            color: opts.color || null,
            textColor: opts.textColor || null,
            icon: opts.icon || null,
            align: opts.align || 'left',
            close: opts.close !== undefined ? opts.close : false,
            onClick: onClick
        };

        if (!this.settings.items) this.settings.items = [];
        this.settings.items.push({ type: 'contentButton', button: btnConfig });
        return this;
    }

    addButtonGroup(builderFn, groupOptions = {}) {
        const builder = new ModalButtonBuilder();
        if (typeof builderFn === 'function') {
            builderFn(builder);
        }
        const buttons = builder._getButtons();

        if (!this.settings.items) this.settings.items = [];
        this.settings.items.push({
            type: 'contentButtonGroup',
            buttons,
            align: groupOptions.align || 'left'
        });
        return this;
    }

    withHeader(show) {
        this.settings.header = show;
        return this;
    }

    withFooter(show) {
        this.settings.footer = show;
        return this;
    }

    withFooterAlign(align) {
        const validAligns = Object.values(Align);
        if (validAligns.includes(align)) {
            this.settings.footerAlign = align;
        } else {
            console.warn(`[GenericModal] Invalid alignment: "${align}". Valid alignments are: ${validAligns.join(', ')}`);
        }
        return this;
    }

    withColumns(labelCol, inputCol) {
        this.settings.labelCol = labelCol;
        this.settings.inputCol = inputCol;
        return this;
    }

    withLabelAlign(align) {
        this.settings.labelAlign = align;
        return this;
    }

    withTextAreaLabelPlacement(placement) {
        const validPlacements = Object.values(TextAreaLabelPlacements);
        if (!placement || validPlacements.includes(placement)) {
            this.settings.textAreaLabelPlacement = placement || ModalDefaults.textAreaLabelPlacement;
        }
        return this;
    }

    withBackdrop(backdrop) {
        const validBackdrops = Object.values(Backdrop);
        if (validBackdrops.includes(backdrop)) {
            this.settings.backdrop = backdrop;
        } else {
            console.warn(`[GenericModal] Invalid backdrop: "${backdrop}". Valid backdrops are: ${validBackdrops.join(', ')}`);
        }
        return this;
    }

    withKeyboard(keyboard) {
        this.settings.keyboard = keyboard;
        return this;
    }

    withDialogStyle(style) {
        this.settings.dialogStyle = style;
        return this;
    }

    withContentStyle(style) {
        this.settings.contentStyle = style;
        return this;
    }

    withBodyStyle(style) {
        this.settings.bodyStyle = style;
        return this;
    }

    withHeaderStyle(style) {
        this.settings.headerStyle = style;
        return this;
    }

    withFooterStyle(style) {
        this.settings.footerStyle = style;
        return this;
    }

    withOptions(options) {
        this.settings = { ...this.settings, ...options };
        return this;
    }

    onAccept(callback) {
        this.settings.onAccept = callback;
        return this;
    }

    onReject(callback) {
        this.settings.onReject = callback;
        return this;
    }

    onShown(callback) {
        this.settings._onShownHooks.push(callback);
        return this;
    }

    onHidden(callback) {
        this.settings._onHiddenHooks.push(callback);
        return this;
    }

    asInfo() {
        return this.withType('info').withAcceptText('OK').withOptions({ showReject: false });
    }

    asSuccess() {
        return this.withType('success').withAcceptText('OK').withOptions({ showReject: false });
    }

    asWarning() {
        return this.withType('warning').withAcceptText('OK').withOptions({ showReject: false });
    }

    asError(title) {
        return this.withType('danger').withTitle(title || 'ERROR OCCURRED').withAcceptText('OK').withOptions({ showReject: false });
    }

    asConfirm() {
        return this.withType('confirm').withAcceptText('YES').withRejectText('NO');
    }

    asForm() {
        this.settings.title = this.settings.title || 'Form';
        this.settings.size = this.settings.size || Sizes.LARGE;
        return this;
    }

    withFields(fields) {
        this.settings.fields = fields;
        return this;
    }

    /**
     * Bind a data object to auto-populate form fields by ID.
     * @param {Object} data Source object (e.g. result.data).
     * @param {Object|Function} [mapping] Optional mapping object { fieldId: 'DataProp' } or mapper function.
     * @returns {GenericModalBuilder}
     */
    withData(data, mapping = {}) {
        this._formData = data || null;
        this._dataMapping = mapping || {};

        if (this._formData && this.settings.fields && this.settings.fields.length > 0) {
            this.settings.fields.forEach(field => {
                this._applyDataToField(field);
            });
        }
        return this;
    }

    _resolveValueFromData(fieldId, fieldOptions = {}) {
        if (!this._formData || !fieldId) return undefined;

        let mappedKey = fieldOptions.dataKey;
        if (!mappedKey && this._dataMapping) {
            if (typeof this._dataMapping === 'function') {
                mappedKey = this._dataMapping(fieldId, this._formData);
            } else if (typeof this._dataMapping === 'object') {
                mappedKey = this._dataMapping[fieldId];
            }
        }

        const data = this._formData;
        const targetKey = mappedKey || fieldId;

        // 1. Direct property lookup
        if (Object.prototype.hasOwnProperty.call(data, targetKey) && data[targetKey] !== undefined) {
            return data[targetKey];
        }

        // 2. PascalCase lookup (e.g. 'name' -> 'Name', 'accountNumber' -> 'AccountNumber')
        const pascalKey = targetKey.charAt(0).toUpperCase() + targetKey.slice(1);
        if (Object.prototype.hasOwnProperty.call(data, pascalKey) && data[pascalKey] !== undefined) {
            return data[pascalKey];
        }

        // 3. camelCase lookup (e.g. 'AccountNo' -> 'accountNo')
        const camelKey = targetKey.charAt(0).toLowerCase() + targetKey.slice(1);
        if (Object.prototype.hasOwnProperty.call(data, camelKey) && data[camelKey] !== undefined) {
            return data[camelKey];
        }

        // 4. Case-insensitive key lookup across data keys
        const lowerTargetKey = targetKey.toLowerCase();
        const foundKey = Object.keys(data).find(k => k.toLowerCase() === lowerTargetKey);
        if (foundKey && data[foundKey] !== undefined) {
            return data[foundKey];
        }

        return undefined;
    }

    _applyDataToField(field) {
        if (!field || !field.id || !this._formData) return;

        const val = this._resolveValueFromData(field.id, field);
        if (val !== undefined) {
            if (field.type === 'checkbox') {
                if (field.value === undefined && typeof val !== 'boolean') {
                    field.value = val;
                } else if (typeof val === 'boolean') {
                    field.checked = val;
                } else {
                    field.value = val;
                }
            } else if (field.type === 'radioGroup') {
                if (field.selectedValue === undefined) {
                    field.selectedValue = val;
                }
            } else {
                if (field.value === undefined) {
                    field.value = val;
                }
            }
        }
    }

    addField(field) {
        if (!this.settings.fields) this.settings.fields = [];
        if (this._formData && field && field.id) {
            this._applyDataToField(field);
        }
        this.settings.fields.push(field);
        if (!this.settings.items) this.settings.items = [];
        this.settings.items.push({ type: 'field', field });
        this._lastField = field;
        return this;
    }

    /**
     * Add an appended button/tag to the last added field.
     * Accepts a callback that receives an InputAppendBuilder.
     * The onClick receives (data, $body, $modal) — data is the current form snapshot.
     */
    withAppend(callback) {
        if (!this._lastField) return this;
        const builder = new InputAppendBuilder();
        if (typeof callback === 'function') callback(builder);
        const items = builder._build();
        if (items.length > 0) {
            if (!this._lastField.append) this._lastField.append = [];
            this._lastField.append.push(...items);
        }
        if (builder._separated) this._lastField.inputGroupLayout = 'separated';
        return this;
    }

    /**
     * Add a prepended button/tag to the last added field.
     * Accepts a callback that receives an InputAppendBuilder.
     * The onClick receives (data, $body, $modal) — data is the current form snapshot.
     */
    withPrepend(callback) {
        if (!this._lastField) return this;
        const builder = new InputAppendBuilder();
        if (typeof callback === 'function') callback(builder);
        const items = builder._build();
        if (items.length > 0) {
            if (!this._lastField.prepend) this._lastField.prepend = [];
            this._lastField.prepend.push(...items);
        }
        if (builder._separated) this._lastField.inputGroupLayout = 'separated';
        return this;
    }

    addColumns(labelOrFn, columnsOrFn) {
        let label = null;
        let fn = labelOrFn;
        if (typeof labelOrFn === 'string') {
            label = labelOrFn;
            fn = columnsOrFn;
        }
        let finalColumns = [];
        let rowGap = null;

        if (typeof fn === 'function') {
            const builder = new GenericRowBuilder();
            fn(builder);
            const built = builder.build();
            finalColumns = built.columns;
            rowGap = built.rowGap;
        } else {
            finalColumns = fn;
        }
        
        return this.addField({ 
            type: FieldTypes.COLUMNS, 
            label: label,
            columns: finalColumns,
            rowGap: rowGap
        });
    }

    addHeading(label, options = {}) {
        const config = typeof options === 'string' ? { color: options } : options;
        return this.addField({ type: FieldTypes.HEADING, label, ...config });
    }

    addHiddenField(id, value, options = {}) {
        return this.addField({ id, type: FieldTypes.HIDDEN, value, ...options });
    }

    addHiddenFields(map = {}) {
        Object.entries(map || {}).forEach(([id, value]) => this.addHiddenField(id, value));
        return this;
    }

    addTextField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.TEXT, ...options });
    }


    addEmailField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.EMAIL, ...options });
    }

    addNumberField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.NUMBER, ...options });
    }

    addSelectField(id, label, selectOptions, options = {}) {
        return this.addField({ id, label, type: FieldTypes.SELECT, options: selectOptions, ...options });
    }

    addDatepickerField(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.DATEPICKER, ...options });
    }

    addTextArea(id, label, options = {}) {
        return this.addField({ id, label, type: FieldTypes.TEXTAREA, ...options });
    }

    withLabelPlacement(placement) {
        if (this._lastField && this._lastField.type === FieldTypes.TEXTAREA) {
            const validPlacements = Object.values(TextAreaLabelPlacements);
            if (!placement || validPlacements.includes(placement)) {
                this._lastField.labelPlacement = placement || ModalDefaults.textAreaLabelPlacement;
            }
        }
        return this;
    }

    addCheckbox(id, label, optionsOrFn, options = {}) {
        const field = { id, label, type: "checkbox" };
        if (typeof optionsOrFn === 'function') {
            const builder = {
                withColor: (c) => { field.color = c; return builder; },
                withLabelColor: (c) => { field.labelColor = c; return builder; },
                withValue: (v) => { field.value = v; return builder; },
                checked: (val = true) => { field.checked = val; return builder; }
            };
            optionsOrFn(builder);
            Object.assign(field, options);
        } else {
            Object.assign(field, optionsOrFn, options);
        }
        this.addField(field);
        return this;
    }

    addRadioGroup(groupId, groupLabel, optionsOrFn, optionsOrFn2 = {}) {
        let radioOptions = [];
        if (typeof optionsOrFn === 'function') {
            const builder = new RadioOptionBuilder();
            optionsOrFn(builder);
            radioOptions = builder.build();
        } else {
            radioOptions = optionsOrFn;
        }

        const field = { id: groupId, label: groupLabel, type: "radioGroup", radioOptions };

        if (typeof optionsOrFn2 === 'function') {
            const builder = {
                inline: (val = true) => { field.inline = val; return builder; },
                withColor: (c) => { field.color = c; return builder; },
                withLabelColor: (c) => { field.labelColor = c; return builder; },
                withSelectedValue: (v) => { field.selectedValue = v; return builder; }
            };
            optionsOrFn2(builder);
        } else {
            Object.assign(field, optionsOrFn2);
        }

        return this.addField(field);
    }

    asReadOnly() {
        this.settings.readOnly = true;
        return this;
    }

    readOnly(isReadOnly = true) {
        this.settings.readOnly = isReadOnly !== false;
        return this;
    }

    readonly(isReadOnly = true) {
        this.settings.readOnly = isReadOnly !== false;
        return this;
    }

    withReadOnly(isReadOnly = true) {
        this.settings.readOnly = isReadOnly !== false;
        return this;
    }

    withReadonly(isReadOnly = true) {
        this.settings.readOnly = isReadOnly !== false;
        return this;
    }

    withReadOnlyStyle(style = 'disabled') {
        this.settings.readOnlyStyle = style;
        return this;
    }

    withReadonlyStyle(style = 'disabled') {
        this.settings.readOnlyStyle = style;
        return this;
    }

    onSubmit(callback) {
        this.settings.onSubmit = callback;
        return this;
    }

    withSubmitText(text) {
        this.settings.submitText = text;
        return this;
    }

    withCancelText(text) {
        this.settings.cancelText = text;
        return this;
    }

    build() {
        const modal = new this.ModalClass(this.settings);
        modal._builder = this;
        return modal;
    }

    show() {
        const modal = this.build();
        modal.show();
        return modal; // return the modal instance so callers can call .hide() or manipulate the modal
    }
}
